import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LuUsers, LuUser, LuLogOut, LuPocketKnife, LuMenu, LuX } from 'react-icons/lu';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';

const navItems = [
  { key: 'dexter', label: 'Dexter', icon: LuPocketKnife, to: '/' },
  { key: 'relationships', label: 'Relationships', icon: LuUsers, to: '/relationships' },
];

const MobileDrawer = ({ open, onClose, navigateTo, activeTab, user, onAccount, onLogout }) => (
  <div className="fixed inset-0 z-[9998] md:hidden">
    <button
      className="absolute inset-0 bg-black/60"
      onClick={onClose}
      aria-label="Close navigation"
    />
    <div
      className={`absolute left-0 top-0 flex h-full w-72 max-w-full flex-col gap-3 border-r border-white/10 bg-[#0c1428] p-4 shadow-2xl ${
        open ? 'animate-slide-in-left' : 'animate-slide-out-left'
      }`}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-200">Menu</span>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-2 text-slate-200 hover:bg-white/5"
          aria-label="Close"
        >
          <LuX className="h-5 w-5" />
        </button>
      </div>

      <div className="flex flex-col gap-2">
        {navItems.map((item) => (
          <button
            key={item.key}
            className={`flex items-center justify-start gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition w-full ${
              activeTab === item.key ? 'bg-sky-500/15 text-slate-50' : 'text-slate-200 hover:bg-white/5'
            }`}
            type="button"
            onClick={() => navigateTo(item.to)}
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </button>
        ))}
      </div>

      <div className="mt-auto flex flex-col gap-2">
        <button
          type="button"
          onClick={() => {
            onAccount();
            onClose();
          }}
          className="flex w-full items-center justify-start gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold whitespace-nowrap text-slate-200 hover:bg-white/5"
        >
          <LuUser className="h-5 w-5" />
          {user?.email || 'Signed in'}
        </button>
        <button
          className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-white/10"
          onClick={() => {
            onLogout();
            onClose();
          }}
        >
          <span className="inline-flex items-center gap-2">
            <LuLogOut className="h-4 w-4" />
            Log out
          </span>
        </button>
      </div>
    </div>
  </div>
);

const DesktopNav = ({ activeTab, onNavigate, onAccount, onLogout, user }) => (
  <aside className="sticky top-0 z-40 hidden h-screen w-64 shrink-0 flex-col items-stretch gap-2 border-r border-white/5 bg-[#0c1428]/95 px-4 py-6 backdrop-blur md:flex">
    <div className="flex w-full flex-col gap-1">
      {navItems.map((item) => (
        <button
          key={item.key}
          className={`flex items-center justify-start gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition w-full ${
            activeTab === item.key ? 'bg-sky-500/15 text-slate-50' : 'text-slate-200 hover:bg-white/5'
          }`}
          type="button"
          onClick={() => onNavigate(item.to)}
        >
          <item.icon className="h-5 w-5" />
          {item.label}
        </button>
      ))}
    </div>

    <div className="mt-auto flex flex-col gap-2 w-full">
      <button
        type="button"
        onClick={onAccount}
        className="flex w-full items-center justify-start gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold whitespace-nowrap text-slate-200 hover:bg-white/5"
      >
        <LuUser className="h-5 w-5" />
        {user?.email || 'Signed in'}
      </button>
      <button
        className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-white/10"
        onClick={onLogout}
      >
        <span className="inline-flex items-center gap-2">
          <LuLogOut className="h-4 w-4" />
          Log out
        </span>
      </button>
    </div>
  </aside>
);

const SideNav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  const activeTab =
    location.pathname.startsWith('/relationship') || location.pathname.startsWith('/relationships')
      ? 'relationships'
      : 'dexter';

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const closeDrawer = () => {
    setClosing(true);
    setOpen(false);
    setTimeout(() => setClosing(false), 250);
  };

  const drawerVisible = open || closing;

  return (
    <>
      {/* Mobile hamburger */}
      {isMobile && (
        <div className="fixed left-0 top-0 z-[9998] flex h-16 w-full items-center gap-3 bg-[#050815]/95 px-4 border-b border-white/10 md:hidden">
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-slate-100 backdrop-blur pointer-events-auto"
            onClick={() => setOpen(true)}
            aria-label="Open navigation"
          >
            <LuMenu className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/dexter.png" alt="Dexter logo" className="h-10 w-10 rounded-lg object-cover" />
            <span className="text-lg font-semibold text-slate-100">Dexter</span>
          </div>
        </div>
      )}

      {/* Desktop nav */}
      <DesktopNav
        activeTab={activeTab}
        onNavigate={(to) => navigate(to)}
        onAccount={() => navigate('/account')}
        onLogout={handleLogout}
        user={user}
      />

      {/* Mobile drawer */}
      {isMobile && drawerVisible && (
        <MobileDrawer
          open={open && !closing}
          onClose={closeDrawer}
          activeTab={activeTab}
          user={user}
          onAccount={() => navigate('/account')}
          onLogout={handleLogout}
          navigateTo={(to) => {
            navigate(to);
            closeDrawer();
          }}
        />
      )}
    </>
  );
};

export default SideNav;
